import { useEffect, useState } from 'react';

import { notifyEnum } from '../constants/constants';
import { checkRol } from '../security';
import {
  notificador,
  proveedorAdministrador,
  proveedorNotificador,
  solicitante,
  aprobador,
  inspector,
  consultaServicios
} from '../security/constantsRols';

// Al final va a ser tanto para solicitud normal como mensualizada
// Puede ser una constante a analizar
const useRequestDetailPermissions = ({ notifyTo, isASDS = false, isOyM = false }) => {
  const [permissions, setPermissions] = useState({});
  const isAdminProvider = checkRol(proveedorAdministrador);
  const isApplicant = checkRol(solicitante);
  const isApprover = checkRol(aprobador);
  const isNotifier = checkRol(notificador);
  const isInspector = checkRol(inspector);
  const isNotifyProvider = checkRol(proveedorNotificador);
  const userView = checkRol(consultaServicios);
  const hasDuoProvider = isAdminProvider && isNotifyProvider;
  const isNotifierOrNotifyToProvider =
    isNotifier ||
    (isNotifyProvider && !isAdminProvider) ||
    (hasDuoProvider && notifyTo === notifyEnum.provider);

  const canEditWells = () =>
    isApplicant ||
    isInspector ||
    isNotifier ||
    (isASDS && isNotifierOrNotifyToProvider) ||
    (!isOyM && isApprover);

  const handleUpdatePermissions = () => {
    const finalPermissions = {
      // Replicar SDS
      replySDS: isApplicant,
      // Dentro del modal que se abre cuando apretamos en el boton `Ver Detalles`
      // 👇 typeAndObjectImputation hace referencia a lo que se ve en una SDS ASDS
      typeAndObjectImputation: {
        visualize: !isOyM || (isOyM && !isAdminProvider && !isNotifyProvider),
        editWells: canEditWells(),
        editObjectImputation: isApplicant || isInspector || isNotifier || isApprover
      },
      workPart: {
        addPositionLine: isNotifierOrNotifyToProvider,
        addProvider: isApplicant,
        addUbication: isNotifierOrNotifyToProvider,
        applyTemplate: isNotifierOrNotifyToProvider,
        // Esto se usa cuando apretamos en `Ver Detalles` en la cabecera de un acordion
        // 👇 Poder agregar pozo a una ubicacion, en lo que seria una ubicacion de una MSDS
        addWellToObjectImputation:
          isApplicant ||
          isInspector ||
          isNotifier ||
          isNotifierOrNotifyToProvider ||
          (!isOyM && isApprover),
        createWorkPart: isNotifierOrNotifyToProvider,
        deletePositionLine: isNotifierOrNotifyToProvider,
        deleteUbication: isNotifierOrNotifyToProvider,
        // Esto se usa cuando apretamos en `Ver Detalles` en la cabecera de un acordion
        // 👇 Poder editar el objeto y tipo de imputacion, en lo que seria una ubicacion de una MSDS
        editObjectImputation: isApplicant || isInspector || isNotifier || isApprover,
        // En principio `headerWorkPartASDS` incluiria lo siguiente:
        // Agregar fecha, editar, cancelar, buscar linea
        // Esto es asi porque es el unico permiso que validamos
        headerWorkPartASDS: isNotifierOrNotifyToProvider,
        simplifiedCharging: {
          visualize: isApplicant || isNotifierOrNotifyToProvider || userView,
          write: isNotifierOrNotifyToProvider
        },
        // positionLine representa la linea con posicion y linea
        // Y se representan los permisos del menu de acciones que tiene cada una
        positionLine: {
          attachedFiles: {
            visualize: true,
            write: isNotifierOrNotifyToProvider || isInspector
          },
          comments: {
            visualize: true,
            write: isApplicant || isNotifierOrNotifyToProvider || isInspector
          },
          sendToApprove: isNotifierOrNotifyToProvider,
          rectifyLine: isNotifierOrNotifyToProvider,
          deleteLine: isNotifierOrNotifyToProvider,
          // En caso que este ordenado por posicion linea
          // Aqui se mostraran las ubicaciones asociadas
          deleteUbication: isNotifierOrNotifyToProvider,
          expertReview: {
            visualize: true,
            actionInspectorButton: isInspector
          },
          // Lo dejo para cuando se integre como parte de los permisos
          programPoint: isNotifyProvider
        },
        editDateInput: hasDuoProvider
      },
      approvers: {
        visualize: true,
        write: isApplicant
      },
      pendingAuthorization: {
        visualize: isInspector,
        write: isInspector
      },
      message: {
        visualize: true,
        write: isApplicant || isAdminProvider || isNotifierOrNotifyToProvider || isInspector
      },
      communicateTo: {
        visualize: true,
        write: isApplicant || isInspector
      },
      attachedFiles: {
        request: {
          delete: isApplicant,
          download: true,
          upload: isApplicant
        },
        workPart: {
          delete: isApprover || isNotifier || isNotifierOrNotifyToProvider,
          download: true,
          upload: isApprover || isNotifier || isNotifierOrNotifyToProvider || isInspector
        }
      }
    };

    setPermissions(finalPermissions);
  };

  useEffect(() => {
    handleUpdatePermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotifierOrNotifyToProvider]);

  return {
    permissions
  };
};

export default useRequestDetailPermissions;
